import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Motion";
import VideoHero from "../components/VideoHero";
import EditorialMarquee from "../components/EditorialMarquee";
import DidYouKnow from "../components/DidYouKnow";
import WhyPuroPack from "../components/WhyPuroPack";
import { PRODUCTS, INDUSTRIES } from "../lib/data";

export default function Home() {
    const reduced = useReducedMotion();
    const introRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: introRef, offset: ["start end", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], reduced ? [0, 0] : [80, -80]);
    const scaleImg = useTransform(scrollYProgress, [0, 1], reduced ? [1, 1] : [1, 1.06]);

    const orgSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "@id": "https://thepuropack.com/#organization",
                name: "The Puro Pack",
                url: "https://thepuropack.com",
                logo: {
                    "@type": "ImageObject",
                    url: "https://thepuropack.com/logo-512.png",
                    width: 512,
                    height: 512,
                },
                slogan: "Your Brand, Our Eco Promise",
                description: "The Puro Pack is a Bangalore-based manufacturer of premium paper salad bowls, soup bowls, kraft food containers, PET flat lids and custom printed sustainable food packaging for restaurants, QSR chains, cloud kitchens and food brands across India.",
                address: { "@type": "PostalAddress", addressLocality: "Bangalore", addressRegion: "Karnataka", addressCountry: "IN" },
                sameAs: [],
                contactPoint: [{
                    "@type": "ContactPoint",
                    contactType: "sales",
                    telephone: "+91-9008456329",
                    availableLanguage: ["en", "hi"],
                    areaServed: ["IN", "AE", "SA", "GB", "DE"],
                }],
            },
            {
                "@type": "LocalBusiness",
                "@id": "https://thepuropack.com/#localbusiness",
                name: "The Puro Pack",
                image: "https://thepuropack.com/logo-512.png",
                url: "https://thepuropack.com",
                telephone: "+91-9008456329",
                priceRange: "$$",
                description: "Manufacturer of eco-friendly paper bowls, salad bowls, soup bowls, brown kraft paper bowls, customized printed paper bowls, ice cream cups, paper containers and PET flat lids in Bangalore, India.",
                address: { "@type": "PostalAddress", addressLocality: "Bangalore", addressRegion: "Karnataka", addressCountry: "IN" },
                areaServed: [{ "@type": "City", name: "Bangalore" }, { "@type": "Country", name: "India" }, { "@type": "Country", name: "United Arab Emirates" }, { "@type": "Country", name: "United Kingdom" }],
                knowsAbout: ["Eco-friendly paper bowls", "Paper salad bowls", "Paper soup bowls", "Brown kraft paper bowls", "Custom printed paper bowls", "Paper ice cream cups", "Paper containers", "PET flat lids", "Sustainable food packaging Bangalore"],
            },
            {
                "@type": "WebSite",
                "@id": "https://thepuropack.com/#website",
                url: "https://thepuropack.com",
                name: "The Puro Pack",
                publisher: { "@id": "https://thepuropack.com/#organization" },
                potentialAction: {
                    "@type": "SearchAction",
                    target: { "@type": "EntryPoint", urlTemplate: "https://thepuropack.com/blog?q={search_term_string}" },
                    "query-input": "required name=search_term_string",
                },
            },
        ],
    };

    return (
        <div data-testid="home-page">
            <Seo
                title="The Puro Pack | Sustainable Food Packaging Manufacturer in Bangalore"
                description="The Puro Pack is a Bangalore-based manufacturer of premium paper salad bowls, soup bowls, kraft food containers, PET flat lids and custom printed sustainable food packaging for restaurants, QSR chains, cloud kitchens and food brands across India."
                path="/"
                schema={orgSchema}
            />

            {/* FULL-BLEED VIDEO HERO */}
            <VideoHero />

            {/* INTRO / EDITORIAL BLOCK */}
            <section ref={introRef} className="relative overflow-hidden">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-20 lg:pb-32 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-7">
                        <div className="font-mono text-xs uppercase tracking-widest text-clay mb-8">[ Chapter 00 · The Brief ]</div>
                        <Reveal>
                            <h2 className="font-serif font-light text-5xl lg:text-8xl leading-[0.96] tracking-[-0.02em] text-forest">
                                A <em className="italic">Food Packaging</em> Manufacturer with the agility of a startup.
                            </h2>
                        </Reveal>
                        <div className="grid md:grid-cols-2 gap-10 mt-14 max-w-3xl">
                            <p className="text-base text-inkgreen/80 leading-relaxed">
                                The Puro Pack manufactures premium paper food packaging including paper salad bowls, soup bowls, kraft containers, PET flat lids and custom printed packaging for restaurants, cloud kitchens, caterers and food brands across India.
                            </p>
                            <div className="flex flex-col items-start gap-4">
                                <Link to="/contact" className="btn-pill btn-solid" data-testid="intro-cta">Talk to production <ArrowUpRight className="w-4 h-4" /></Link>
                                <Link to="/sustainable-packaging" className="btn-pill" data-testid="intro-sust-cta">See sustainability →</Link>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <motion.div style={{ y: yImg, scale: scaleImg }} className="image-frame aspect-[3/4]">
                            <img src="https://customer-assets-v7afamib.emergentagent.net/job_eco-pack-portal-1/artifacts/iydhs0gu_AdobeStock_1518566356%20copy.webp" alt="The Puro Pack branded white paper bowl with Your Brand, Our Eco Promise slogan" loading="lazy" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <EditorialMarquee items={["Food-Safe Paper Packaging", "PE & Aqueous Coated", "Made in Bangalore", "Green Technology Partner", "Est. 2025"]} />

            {/* DID YOU KNOW */}
            <DidYouKnow />

            {/* MANIFESTO */}
            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <Reveal>
                    <div className="grid lg:grid-cols-12 gap-8 mb-16">
                        <div className="lg:col-span-4 font-mono text-xs uppercase tracking-widest text-stone">[ Manifesto ]</div>
                        <h2 className="lg:col-span-8 font-serif text-4xl lg:text-6xl leading-[1.05] tracking-tight text-forest">
                            Four convictions we <em className="italic">refuse</em> to compromise on.
                        </h2>
                    </div>
                </Reveal>

                {[
                    { n: "01", h: "Food-safe, by design.", b: "Every product leaves our line with either a food-safe PE coating or a water-based Aqueous coating — chosen for the temperature, shelf-life and end-of-life your kitchen needs." },
                    { n: "02", h: "Made close, shipped far.", b: "Our factory in Bangalore lets us keep quality tight and lead-times short — from an idea on Monday to sample kits on Friday." },
                    { n: "03", h: "Print is packaging.", b: "For cloud kitchens especially, the bowl is the storefront. Our presses treat every SKU like a magazine cover." },
                    { n: "04", h: "Boring things done seriously.", b: "GSM tolerance, leak tests, cure temperatures. The unglamorous parts of manufacturing are the parts we obsess over." },
                ].map((m, i) => (
                    <Reveal key={m.n} delay={i * 0.05}>
                        <div className="grid lg:grid-cols-12 gap-8 border-t border-forest/30 py-10">
                            <div className="lg:col-span-2 font-mono text-5xl lg:text-6xl text-clay">[{m.n}]</div>
                            <h3 className="lg:col-span-5 font-serif text-3xl lg:text-4xl leading-tight text-forest tracking-tight">{m.h}</h3>
                            <p className="lg:col-span-5 text-inkgreen/80 leading-relaxed max-w-xl">{m.b}</p>
                        </div>
                    </Reveal>
                ))}
            </section>

            {/* WHY THE PURO PACK */}
            <WhyPuroPack />

            {/* PRODUCTS */}
            <section className="bg-sand border-y border-forest">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
                    <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-stone mb-4">[ Product Catalogue ]</div>
                            <h2 className="font-serif text-5xl lg:text-7xl leading-none tracking-tight text-forest">Built for <em className="italic">every</em> plate.</h2>
                        </div>
                        <Link to="/sustainable-packaging" className="btn-pill" data-testid="products-cta">View Full Catalogue →</Link>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
                        {PRODUCTS.map((p, i) => (
                            <Reveal key={p.slug} delay={i * 0.06}>
                                <Link to={`/products/${p.slug}`} className="block group" data-testid={`product-card-${p.slug}`}>
                                    <div className="image-frame aspect-[4/5] lg:aspect-[5/4]">
                                        <img src={p.image} alt={`${p.title} by The Puro Pack`} loading="lazy" />
                                    </div>
                                    <div className="pt-6 flex items-start justify-between gap-4">
                                        <div>
                                            <div className="font-mono text-xs text-clay uppercase tracking-widest mb-1">Chapter {p.chapter}</div>
                                            <h3 className="font-serif text-3xl lg:text-4xl text-forest tracking-tight">{p.title}</h3>
                                            <p className="mt-2 text-sm text-inkgreen/75 max-w-md">{p.tagline}</p>
                                        </div>
                                        <ArrowUpRight className="text-forest group-hover:text-clay transition-colors flex-shrink-0" />
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* INDUSTRIES STRIP */}
            <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <Reveal>
                    <div className="mb-14">
                        <div className="font-mono text-xs uppercase tracking-widest text-stone mb-4">[ Industries We Serve ]</div>
                        <h2 className="font-serif text-5xl lg:text-7xl leading-none tracking-tight text-forest max-w-4xl">The kitchens that <em className="italic">trust</em> paper.</h2>
                    </div>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-forest/30">
                    {INDUSTRIES.map((ind) => (
                        <div key={ind.chapter} className="border-r border-b border-forest/30 p-8 lg:p-10 hover:bg-sand transition-colors">
                            <div className="font-mono text-4xl text-muted-foreground/60 mb-4">[{ind.chapter}]</div>
                            <h3 className="font-serif text-2xl lg:text-3xl text-forest tracking-tight mb-3">{ind.title}</h3>
                            <p className="text-sm text-inkgreen/75 leading-relaxed">{ind.body}</p>
                        </div>
                    ))}
                </div>
                <div className="mt-10">
                    <Link to="/industries" className="btn-pill" data-testid="industries-cta">See all industries →</Link>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-forest text-bone">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <div className="font-mono text-xs uppercase tracking-widest opacity-60 mb-6">[ Talk to production ]</div>
                        <h2 className="font-serif text-5xl lg:text-7xl leading-[1.02] tracking-tight">
                            Have a launch on the calendar? <em className="italic">Ship samples in 72 hours.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <Link to="/contact" className="btn-pill border-bone text-bone hover:bg-bone hover:text-forest" data-testid="home-final-cta">
                            Start an Enquiry <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
